import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { Shield, LayoutDashboard, Building2, LogOut, Menu, X, Sun, Moon, HelpCircle, ScrollText, Bell, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { supabase } from '../../lib/supabase';
import AdminDashboard from './AdminDashboard';
import AdminAccounts from './AdminAccounts';
import AdminHelp from './AdminHelp';
import AdminLogs from './AdminLogs';
import AdminSalesGuide from './AdminSalesGuide';
import AdminGymDetail from './AdminGymDetail';

const navItems = [
  { path: '/admin', label: '대시보드', icon: LayoutDashboard, end: true },
  { path: '/admin/accounts', label: '계정 관리', icon: Building2 },
  { path: '/admin/logs', label: '활동 로그', icon: ScrollText },
  { path: '/admin/sales', label: '영업 가이드', icon: BookOpen },
  { path: '/admin/help', label: '도움말', icon: HelpCircle, end: false },
];

// Admin-level supabase helpers
export async function adminGetStats() {
  const { data, error } = await supabase.rpc('admin_get_stats');
  if (error) return null;
  return data;
}
export async function adminGetAccounts() {
  const { data, error } = await supabase.rpc('admin_get_accounts');
  if (error) { console.error('[adminGetAccounts]', error); return []; }
  return data || [];
}
export async function adminGetGyms() {
  const { data, error } = await supabase.rpc('admin_get_gyms');
  if (error) { console.error('[adminGetGyms]', error); return []; }
  return data || [];
}
export async function adminApproveAccount(userId) {
  const { error } = await supabase.rpc('admin_approve_account', { p_user_id: userId });
  return { error };
}
export async function adminSuspendAccount(userId) {
  const { error } = await supabase.rpc('admin_suspend_account', { p_user_id: userId });
  return { error };
}
export async function adminRestoreAccount(userId) {
  const { error } = await supabase.rpc('admin_restore_account', { p_user_id: userId });
  return { error };
}
export async function adminUpdateGymPlan(gymId, plan, expiresAt, note) {
  const { error } = await supabase.rpc('admin_update_gym_plan', {
    p_gym_id: gymId,
    p_plan: plan,
    p_expires_at: expiresAt || null,
    p_note: note || null,
  });
  return { error };
}
export async function adminGetPaymentRequests() {
  const { data, error } = await supabase.rpc('admin_get_payment_requests');
  if (error) { console.error('[adminGetPaymentRequests]', error); return []; }
  return data || [];
}
export async function adminConfirmPayment(requestId, adminNote) {
  const { error } = await supabase.rpc('admin_confirm_payment', {
    p_request_id: requestId,
    p_admin_note: adminNote || null,
  });
  return { error };
}
export async function adminRejectPayment(requestId, adminNote) {
  const { error } = await supabase.rpc('admin_reject_payment', {
    p_request_id: requestId,
    p_admin_note: adminNote || null,
  });
  return { error };
}
export async function adminGetPlanLogs(gymId) {
  const { data, error } = await supabase.rpc('admin_get_plan_logs', { p_gym_id: gymId });
  if (error) return [];
  return data || [];
}
export async function adminDeleteGym(gymId) {
  const { error } = await supabase.rpc('admin_delete_gym', { p_gym_id: gymId });
  return { error };
}
export async function adminDeleteAccount(userId) {
  const { error } = await supabase.rpc('admin_delete_account', { p_user_id: userId });
  return { error };
}
export async function adminRestoreGym(gymId) {
  const { error } = await supabase.rpc('admin_restore_gym', { p_gym_id: gymId });
  return { error };
}
export async function adminGetDeletedGyms() {
  const { data, error } = await supabase.rpc('admin_get_deleted_gyms');
  if (error) { console.error('[adminGetDeletedGyms]', error); return []; }
  return data || [];
}
export async function adminGetTrialRequests() {
  const { data, error } = await supabase
    .from('trial_requests')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) return [];
  return data || [];
}
export async function adminUpdateTrialStatus(id, status, note) {
  const { error } = await supabase.rpc('admin_update_trial_status', {
    p_id: id,
    p_status: status,
    p_note: note || null,
  });
  return { error };
}
export async function adminGetActionLogs() {
  const { data, error } = await supabase.rpc('admin_get_action_logs');
  if (error) { console.error('[adminGetActionLogs]', error); return []; }
  return data || [];
}
export async function adminGenerateInviteCode() {
  const { data, error } = await supabase.rpc('admin_generate_invite_code');
  if (error) return { code: null, error };
  return { code: data, error: null };
}
export async function adminGetInviteCodes() {
  const { data, error } = await supabase.rpc('admin_get_invite_codes');
  if (error) return [];
  return data || [];
}
export async function adminGetAllPlanLogs() {
  const { data, error } = await supabase.rpc('admin_get_all_plan_logs');
  if (error) { console.error('[adminGetAllPlanLogs]', error); return []; }
  return data || [];
}
export async function adminGetAllPaymentRequests() {
  const { data, error } = await supabase.rpc('admin_get_all_payment_requests');
  if (error) { console.error('[adminGetAllPaymentRequests]', error); return []; }
  return data || [];
}
export async function adminSendPushToGym(gymId, title, body, url) {
  const { data, error } = await supabase.functions.invoke('send-push', {
    body: { gym_id: gymId, title, body, url },
  });
  return { data, error };
}

export default function AdminPanel() {
  const { signOut, user } = useAuth();
  const { darkMode, setDarkMode } = useApp();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notifRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const onFocus = () => fetchNotifications();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function fetchNotifications() {
    const [trialsRes, accountsRes, paymentsRes] = await Promise.all([
      supabase.from('trial_requests').select('id, name, gym_name, created_at').eq('status', 'pending').order('created_at', { ascending: false }),
      supabase.rpc('admin_get_accounts').then(({ data }) => (data || []).filter(a => a.status === 'pending')),
      supabase.rpc('admin_get_payment_requests').then(({ data }) => (data || []).filter(p => p.status === 'pending')),
    ]);
    const items = [
      ...(trialsRes.data || []).map(t => ({ id: `trial-${t.id}`, type: 'trial', label: `체험 신청: ${t.name} (${t.gym_name})`, created_at: t.created_at, path: '/admin/accounts' })),
      ...accountsRes.map(a => ({ id: `account-${a.id}`, type: 'account', label: `가입 승인 대기: ${a.name}`, created_at: a.created_at, path: '/admin/accounts' })),
      ...paymentsRes.map(p => ({ id: `payment-${p.id}`, type: 'payment', label: `납부 확인 대기: ${p.gym_name}`, created_at: p.created_at, path: '/admin' })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setNotifications(items);
  }

  const unreadCount = notifications.length;

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* 모바일 오버레이 */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative w-64 bg-white dark:bg-gray-800 flex flex-col shadow-xl">
            <Sidebar mobile onClose={() => setSidebarOpen(false)} signOut={signOut} user={user} darkMode={darkMode} setDarkMode={setDarkMode} />
          </aside>
        </div>
      )}

      {/* 데스크탑 사이드바 */}
      <aside className="hidden lg:flex lg:flex-col w-60 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-shrink-0">
        <Sidebar signOut={signOut} user={user} darkMode={darkMode} setDarkMode={setDarkMode} />
      </aside>

      {/* 메인 */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* 헤더 */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 lg:px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-gray-900 dark:text-white text-sm">Admin Console</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {/* 알림 벨 */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(v => !v)}
                className="relative p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="absolute right-0 top-10 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                    <span className="font-semibold text-sm text-gray-900 dark:text-white">미처리 알림</span>
                    <button onClick={fetchNotifications} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">새로고침</button>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-400">미처리 항목이 없습니다</div>
                  ) : (
                    <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                      {notifications.map(n => (
                        <li key={n.id}>
                          <button
                            onClick={() => { navigate(n.path); setNotifOpen(false); }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex items-start gap-2">
                              <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                                n.type === 'trial' ? 'bg-blue-500' :
                                n.type === 'account' ? 'bg-yellow-500' : 'bg-green-500'
                              }`} />
                              <div>
                                <p className="text-sm text-gray-800 dark:text-gray-200">{n.label}</p>
                                <p className="text-xs text-gray-400 mt-0.5">{new Date(n.created_at).toLocaleDateString('ko-KR')}</p>
                              </div>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <button onClick={() => setDarkMode(!darkMode)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <span className="text-xs text-gray-400 hidden sm:block">{user?.email}</span>
          </div>
        </header>

        {/* 페이지 */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Routes>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/accounts" element={<AdminAccounts />} />
            <Route path="/admin/gyms/:gymId" element={<AdminGymDetail />} />
            <Route path="/admin/trials" element={<Navigate to="/admin/accounts" replace />} />
            <Route path="/admin/logs" element={<AdminLogs />} />
            <Route path="/admin/sales" element={<AdminSalesGuide />} />
            <Route path="/admin/help" element={<AdminHelp />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Sidebar({ mobile, onClose, signOut, user }) {
  return (
    <nav className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-200 dark:border-gray-700">
        <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-gray-900 dark:text-white text-sm">Admin Console</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">GYM Manager</div>
        </div>
        {mobile && (
          <button onClick={onClose} className="ml-auto text-gray-400 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4 px-3">
        <ul className="space-y-1">
          {navItems.map(({ path, label, icon: Icon, end }) => (
            <li key={path}>
              <NavLink
                to={path}
                end={end}
                onClick={() => mobile && onClose?.()}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-200'
                  }`
                }
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      <div className="px-3 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
        <button
          onClick={signOut}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          로그아웃
        </button>
      </div>
    </nav>
  );
}
